import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Target, Hammer, Plus, Loader2, Globe2, X } from 'lucide-react';
import { useAuth, formatApiError } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ----- Pace Ring (SVG progress ring with bold Montserrat center) -----
const PaceRing = ({ value = 0, max = 10, label, sublabel, size = 180, testId }) => {
  const r = (size / 2) - 14;
  const cx = size / 2;
  const cy = size / 2;
  const c = 2 * Math.PI * r;
  const safe = Math.max(0, Math.min(max, value));
  const pct = max > 0 ? safe / max : 0;
  const offset = c - pct * c;
  const complete = pct >= 1;
  const color = complete ? '#D4AF37' : pct > 0.5 ? '#C8A95A' : '#8A6F2F';

  return (
    <div data-testid={testId} className="relative inline-flex flex-col items-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={cx} cy={cy} r={r} stroke="#1a1a1a" strokeWidth="3" fill="none" />
        <circle
          cx={cx} cy={cy} r={r}
          stroke={color}
          strokeWidth="3"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.0s cubic-bezier(0.22, 1, 0.36, 1)' }}
        />
        {/* Quota anchor dot at top */}
        <circle cx={cx} cy={cy - r} r={complete ? 5 : 2.5} fill={complete ? '#D4AF37' : '#444'} transform={`rotate(90 ${cx} ${cy})`} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="font-display text-5xl font-light leading-none" style={{ color }}>
          {safe}
          <span className="text-[#555] text-2xl">/{max}</span>
        </div>
        <div className="text-[9px] tracking-[0.4em] uppercase mt-2 text-[#A3A3A3]">{label}</div>
      </div>
      {sublabel && (
        <div className="mt-3 text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{sublabel}</div>
      )}
    </div>
  );
};

// ----- Animated counting number ticker -----
const Ticker = ({ value, prefix = '$', duration = 1200, className = '' }) => {
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);
  const targetRef = useRef(value);

  useEffect(() => {
    fromRef.current = display;
    targetRef.current = value;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const elapsed = t - start;
      const progress = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = fromRef.current + (targetRef.current - fromRef.current) * eased;
      setDisplay(current);
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const formatted = Math.round(display).toLocaleString();
  return <span className={className}>{prefix}{formatted}</span>;
};

const SIGNAL_TYPES = ['Lagos Sourced', 'Hawaii Sourced', 'Diaspora Contact', 'Capital Lead', 'Comp Logged', 'Other'];

export const PaceTracker = () => {
  const { authHeaders, user } = useAuth();
  const [pace, setPace] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logging, setLogging] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [signalType, setSignalType] = useState('Lagos Sourced');
  const [corridor, setCorridor] = useState('Bridge');
  const [note, setNote] = useState('');

  const fetchAll = async () => {
    try {
      const [p, l] = await Promise.all([
        axios.get(`${API}/pace/today`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API}/pace/legacy-pulse`),
      ]);
      setPace(p.data);
      setPulse(l.data);
    } catch (err) {
      toast.error('Pace tracker offline.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchAll();
    // Poll legacy pulse every 30s for live ticker feel
    const t = setInterval(() => {
      axios.get(`${API}/pace/legacy-pulse`).then((r) => setPulse(r.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user]);

  const submitSignal = async (e) => {
    e?.preventDefault();
    setLogging(true);
    try {
      await axios.post(
        `${API}/pace/signal`,
        { signal_type: signalType, target_corridor: corridor, note: note || null },
        { headers: authHeaders(), withCredentials: true }
      );
      setNote('');
      setShowLog(false);
      await fetchAll();
      toast.success('Signal logged. Pace ascends.');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Signal rejected.');
    } finally {
      setLogging(false);
    }
  };

  if (loading || !pace || !pulse) {
    return (
      <div className="sov-border bg-[#0A0A0A] p-10 mb-12 min-h-[280px] flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-[#D4AF37]" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div data-testid="pace-tracker" className="mb-16">
      {/* Header */}
      <div className="flex items-end justify-between mb-8 border-b border-[#D4AF37]/15 pb-4">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2">Sovereign Pace Tracker</div>
          <div className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Montserrat' }}>
            {pace.quota_complete ? 'Quota cleared.' : 'Daily strike in progress.'}
          </div>
        </div>
        <div className="text-right hidden md:block">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-1">7-Day Discipline</div>
          <div className="font-display text-3xl text-[#D4AF37]">{pace.discipline_score_7d}<span className="text-[#555] text-xl">/100</span></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
        {/* Daily Strike Quota */}
        <div className="lg:col-span-7 sov-border bg-[#0A0A0A] p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] flex items-center gap-2">
              <Target size={12} strokeWidth={1.5} /> Daily Strike Quota
            </div>
            <button
              onClick={() => setShowLog(!showLog)}
              data-testid="pace-log-strike-btn"
              className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#D4AF37] transition-colors flex items-center gap-2 border border-[#D4AF37]/30 px-4 py-2"
            >
              {showLog ? <><X size={12} strokeWidth={1.5} /> Cancel</> : <><Plus size={12} strokeWidth={1.5} /> Log Strike</>}
            </button>
          </div>

          {showLog ? (
            <form onSubmit={submitSignal} className="space-y-6 fade-up" data-testid="pace-log-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Signal Type</label>
                  <select data-testid="pace-signal-type" className="input-sovereign cursor-pointer"
                    value={signalType} onChange={(e) => setSignalType(e.target.value)}>
                    {SIGNAL_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Corridor</label>
                  <select data-testid="pace-corridor" className="input-sovereign cursor-pointer"
                    value={corridor} onChange={(e) => setCorridor(e.target.value)}>
                    <option value="Bridge">Bridge</option>
                    <option value="Nigeria">Nigeria</option>
                    <option value="USA">USA</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Note (optional)</label>
                <input data-testid="pace-note" className="input-sovereign" value={note}
                  onChange={(e) => setNote(e.target.value)} placeholder="Subject, contact, or comp reference" />
              </div>
              <button type="submit" disabled={logging} data-testid="pace-submit-btn" className="btn-sovereign">
                {logging ? <><Loader2 size={14} className="animate-spin" /> <span>Logging</span></> : <><span>Confirm Strike</span> <Plus size={14} strokeWidth={1.5} /></>}
              </button>
            </form>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-6 items-center">
                <div className="flex justify-center">
                  <PaceRing
                    testId="pace-ring-signals"
                    value={pace.signals_today}
                    max={pace.signals_quota}
                    label="Signals"
                    sublabel="Global Bridge"
                    size={200}
                  />
                </div>
                <div className="flex justify-center">
                  <PaceRing
                    testId="pace-ring-refits"
                    value={pace.refits_today}
                    max={pace.refits_quota}
                    label="Refits"
                    sublabel="Property Analysis"
                    size={200}
                  />
                </div>
              </div>

              {/* 7-day streak */}
              <div className="mt-10 pt-8 border-t border-[#D4AF37]/15">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-4">Last 7 Days</div>
                <div className="grid grid-cols-7 gap-3">
                  {pace.streak.map((d) => (
                    <div key={d.iso} className="text-center">
                      <div className={`h-12 mb-2 border ${d.quota_hit ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-[#2a2a2a]'}`}
                        style={{ position: 'relative' }}>
                        {!d.quota_hit && d.signals > 0 && (
                          <div
                            className="absolute bottom-0 left-0 right-0 bg-[#D4AF37]/40"
                            style={{ height: `${Math.min(100, (d.signals / pace.signals_quota) * 100)}%` }}
                          />
                        )}
                      </div>
                      <div className="text-[9px] tracking-[0.2em] uppercase text-[#A3A3A3]">{d.date}</div>
                      <div className="text-[10px] text-[#F5F5F5] font-light mt-1">{d.signals}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Legacy Pulse */}
        <div className="lg:col-span-5 sov-border bg-[#0A0A0A] p-8 relative overflow-hidden">
          <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full border border-[#D4AF37]/10" />
          <div className="absolute -right-20 -bottom-20 w-64 h-64 rounded-full border border-[#D4AF37]/5" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2">
              <Globe2 size={12} strokeWidth={1.5} />
              <span>Legacy Pulse</span>
              <span className="ml-2 inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-pulse"></span>
                <span className="text-[8px] text-[#A3A3A3]">LIVE</span>
              </span>
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-3">CyberKids Fund · 10% Tax</div>

            <div className="font-display text-5xl md:text-6xl font-light leading-none text-[#D4AF37] mb-4" data-testid="legacy-pulse-amount">
              <Ticker value={pulse.accrued_to_cyberkids_usd} prefix="$" />
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] italic font-light mb-8">
              "{pulse.doctrine}"
            </div>

            <div className="space-y-4 pt-6 border-t border-[#D4AF37]/15">
              <PulseRow label="Vessel Throughput" value={pulse.vessel_throughput_usd} />
              <PulseRow label="AUM Curated" value={pulse.aum_usd} />
              <PulseRow label="Flip Throughput" value={pulse.flip_throughput_usd} />
              <PulseRow label="Valuation Volume" value={pulse.valuation_throughput_usd} />
            </div>

            <div className="mt-8 grid grid-cols-3 gap-3 pt-6 border-t border-[#D4AF37]/15">
              {[
                { l: 'Properties', v: pulse.counts.properties },
                { l: 'Flips', v: pulse.counts.flips },
                { l: 'Valuations', v: pulse.counts.valuations },
              ].map((s) => (
                <div key={s.l} className="text-center">
                  <div className="font-display text-2xl text-[#F5F5F5]">{s.v}</div>
                  <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3] mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent signals */}
      {pace.recent_signals.length > 0 && !showLog && (
        <div className="mt-6 sov-border bg-[#0A0A0A] p-6">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4 flex items-center gap-2">
            <Hammer size={12} strokeWidth={1.5} /> Today's Strikes
          </div>
          <div className="space-y-3">
            {pace.recent_signals.map((s) => (
              <div key={s.id} className="flex items-center justify-between border-b border-[#D4AF37]/10 pb-3 last:border-b-0">
                <div className="flex items-center gap-4">
                  <div className="w-1 h-1 bg-[#D4AF37]"></div>
                  <span className="text-sm font-light tracking-wide">{s.signal_type}</span>
                  {s.note && <span className="text-xs text-[#A3A3A3] italic">— {s.note}</span>}
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
                  {s.target_corridor}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PulseRow = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{label}</span>
    <span className="font-display text-lg text-[#F5F5F5]">
      <Ticker value={value} prefix="$" duration={900} />
    </span>
  </div>
);
